import { supabase } from '../config/supabase';

/**
 * Formats the shopping list for Instacart API
 * @param {Object} shoppingList - The shopping list object from EasyMeals
 * @returns {Array} - Array of line items formatted for Instacart
 */
export const formatShoppingListForInstacart = (shoppingList) => {
  if (!shoppingList) return [];
  
  const lineItems = [];
  
  // Iterate through each category in the shopping list
  Object.entries(shoppingList).forEach(([category, items]) => {
    if (!Array.isArray(items)) return;
    
    items.forEach(item => {
      if (!item || !item.item) return;
      
      // Extract quantity and unit if available
      let quantity = 1;
      let unit = "each";
      
      if (item.quantity) {
        // Try to parse the quantity as a number
        const parsedQuantity = parseFloat(item.quantity);
        if (!isNaN(parsedQuantity)) {
          quantity = parsedQuantity;
        }
      }
      
      // Add the item to the line items array
      lineItems.push({
        name: item.item,
        quantity,
        unit
      });
    });
  });
  
  return lineItems;
};

// Determine if we're in development or production
const isDevelopment = import.meta.env.DEV;
// Use mock in development by default, but allow override via env variable
const USE_MOCK = isDevelopment && import.meta.env.VITE_USE_REAL_INSTACART !== 'true';
// Get the API URL - use a proxy in production, direct in development with real API
// Updated to use connect.instacart.com instead of api.instacart.com
const INSTACART_API_URL = isDevelopment && !USE_MOCK
  ? 'https://connect.instacart.com/idp/v1/products/products_link'
  : '/api/instacart/products-link';

/**
 * Creates an Instacart products link with retry logic
 * @param {Object} shoppingList - The shopping list object from EasyMeals
 * @param {string} title - The title for the shopping list
 * @param {number} retries - Number of retry attempts (default: 2)
 * @returns {Promise<string>} - The Instacart products link URL
 */
export const createInstacartProductsLink = async (shoppingList, title = "EasyMeals Shopping List", retries = 2) => {
  // For local development, use a mock implementation to bypass CORS issues
  if (USE_MOCK) {
    console.log('MOCK MODE: Would have sent to Instacart:', {
      title,
      line_items: formatShoppingListForInstacart(shoppingList),
      landing_page_configuration: {
        partner_linkback_url: window.location.origin + "/shopping-list"
      }
    });
    
    // Simulate a delay to mimic an API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return a mock Instacart URL with affiliate parameters
    return "https://www.instacart.com/store/partner/list/mock-list-id?irad=1744632&irmp=6071614";
  }
  
  // In development with real API, we need to format the shopping list
  // In production, the serverless function will handle the formatting
  const requestBody = isDevelopment && !USE_MOCK
    ? {
        title,
        line_items: formatShoppingListForInstacart(shoppingList),
        landing_page_configuration: {
          partner_linkback_url: window.location.origin + "/shopping-list"
        }
      }
    : {
        shoppingList,
        title
      };
  
  // Retry logic for transient network failures
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt > 0) {
        // Exponential backoff: wait 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000;
        console.log(`Retrying Instacart API call (attempt ${attempt + 1}/${retries + 1}) after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      
      // Make the API call
      const response = await fetch(INSTACART_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(isDevelopment && !USE_MOCK ? {
            'Authorization': `Bearer ${import.meta.env.VITE_INSTACART_API_KEY}`
          } : {})
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        let errorMessage = `Instacart API error: ${response.status} ${response.statusText}`;
        let errorData = {};
        
        try {
          errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse the error response, use the status text
          const responseText = await response.text().catch(() => '');
          if (responseText) {
            errorMessage = `${errorMessage} - ${responseText}`;
          }
        }
        
        // Don't retry on client errors (4xx), only on server errors (5xx) or network errors
        if (response.status >= 400 && response.status < 500) {
          const apiError = new Error(errorMessage);
          apiError.isApiError = true;
          apiError.status = response.status;
          apiError.details = errorData;
          throw apiError;
        }
        
        // Retry on server errors
        if (attempt < retries) {
          console.warn(`Instacart API server error (${response.status}), will retry...`);
          continue;
        }
        
        const serverError = new Error(errorMessage);
        serverError.isApiError = true;
        serverError.status = response.status;
        serverError.details = errorData;
        throw serverError;
      }
      
      const data = await response.json();
      
      if (!data.products_link_url) {
        throw new Error('Invalid response from Instacart API: missing products_link_url');
      }
      
      const baseUrl = data.products_link_url;
      
      // Append affiliate tracking parameters
      const url = new URL(baseUrl);
      url.searchParams.append('irad', '1744632');
      url.searchParams.append('irmp', '6071614');
      
      console.log('DEBUG: Generated Instacart URL with affiliate tracking:', url.toString());
      
      return url.toString();
    } catch (error) {
      // Check if this is a network error (not an API error)
      const isNetworkError = !error.isApiError && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('ERR_NAME_NOT_RESOLVED') ||
        error.message.includes('ERR_INTERNET_DISCONNECTED') ||
        error.name === 'TypeError' && error.message.includes('fetch')
      );
      
      if (isNetworkError) {
        if (attempt < retries) {
          console.warn(`Network error connecting to Instacart API, will retry...`, error.message);
          continue;
        }
        
        // Final attempt failed - throw network error
        const networkError = new Error('Unable to connect to Instacart. Please check your internet connection and try again.');
        networkError.isNetworkError = true;
        networkError.originalError = error;
        throw networkError;
      }
      
      // For API errors or other errors, throw immediately
      throw error;
    }
  }
  
  // This should never be reached, but TypeScript/flow checking
  throw new Error('Failed to create Instacart products link after all retry attempts');
};

/**
 * Saves an Instacart link to the database
 * @param {string} userId - The user ID (optional for anonymous users)
 * @param {string} mealPlanId - The meal plan ID (optional)
 * @param {string} productsLinkUrl - The Instacart products link URL
 * @param {string} sessionId - Session ID for anonymous users (optional)
 * @returns {Promise<Object>} - The saved Instacart link
 */
export const saveInstacartLink = async (userId = null, mealPlanId = null, productsLinkUrl, sessionId = null) => {
  try {
    console.log('DEBUG: saveInstacartLink called with:', { 
      userId, mealPlanId, productsLinkUrl, sessionId 
    });
    
    const isAnonymous = !userId;
    const sessionIdToUse = isAnonymous ? (sessionId || generateSessionId()) : null;
    
    console.log('DEBUG: Prepared data:', { 
      isAnonymous, sessionIdToUse 
    });
    
    // Log the Supabase URL and key (just existence, not the actual key)
    console.log('DEBUG: Supabase URL:', supabase.supabaseUrl);
    console.log('DEBUG: Supabase key exists:', !!supabase.supabaseKey);
    
    const insertData = {
      user_id: userId,
      meal_plan_id: mealPlanId,
      products_link_url: productsLinkUrl,
      session_id: sessionIdToUse,
      is_anonymous: isAnonymous,
      status: 'active',
      expires_in: 7,
      created_at: new Date().toISOString()
    };
    
    console.log('DEBUG: Insert data:', insertData);
    
    const { data, error } = await supabase
      .from('instacart_links')
      .insert([insertData])
      .select();
      
    console.log('DEBUG: Insert result:', { data, error });
    
    if (error) {
      console.error('DEBUG: Error details:', error);
      
      // Check if this is a network/DNS error
      const isNetworkError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_NAME_NOT_RESOLVED') ||
        error.message.includes('NetworkError')
      );
      
      if (isNetworkError) {
        const networkError = new Error('Unable to save link to database. Please check your internet connection.');
        networkError.isNetworkError = true;
        networkError.isSupabaseError = true;
        networkError.originalError = error;
        throw networkError;
      }
      
      // For other Supabase errors, throw as-is
      const supabaseError = new Error(error.message || 'Database error occurred');
      supabaseError.isSupabaseError = true;
      supabaseError.originalError = error;
      throw supabaseError;
    }
    
    if (!data || data.length === 0) {
      throw new Error('No data returned from database insert');
    }
    
    return data[0];
  } catch (error) {
    console.error('DEBUG: Exception:', error);
    
    // Re-throw if it's already been wrapped
    if (error.isNetworkError || error.isSupabaseError) {
      throw error;
    }
    
    // Wrap unexpected errors
    const wrappedError = new Error(error.message || 'Unexpected error saving Instacart link');
    wrappedError.originalError = error;
    throw wrappedError;
  }
};

/**
 * Gets the most recent Instacart link for a meal plan
 * @param {string} userId - The user ID
 * @param {string} mealPlanId - The meal plan ID
 * @returns {Promise<Object|null>} - The Instacart link or null if not found
 */
export const getInstacartLink = async (userId, mealPlanId) => {
  try {
    const { data, error } = await supabase
      .from('instacart_links')
      .select('*')
      .eq('user_id', userId)
      .eq('meal_plan_id', mealPlanId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);
      
    if (error) throw error;
    return data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Error getting Instacart link:', error);
    return null;
  }
};

/**
 * Tracks a click on an Instacart link
 * @param {string} linkId - The Instacart link ID
 * @param {string} userId - The user ID (optional)
 * @param {string} mealPlanId - The meal plan ID (optional)
 * @param {string} sessionId - The session ID for anonymous users (optional)
 * @returns {Promise<Object>} - The saved click data
 */
export const trackInstacartLinkClick = async (linkId, userId = null, mealPlanId = null, sessionId = null) => {
  try {
    console.log('DEBUG: trackInstacartLinkClick called with:', { 
      linkId, userId, mealPlanId, sessionId 
    });
    
    const isAnonymous = !userId;
    const sessionIdToUse = isAnonymous ? (sessionId || generateSessionId()) : null;
    
    console.log('DEBUG: Prepared click data:', { 
      isAnonymous, sessionIdToUse 
    });
    
    // First, get the link data to retrieve the products_link_url
    const { data: linkData, error: linkError } = await supabase
      .from('instacart_links')
      .select('products_link_url')
      .eq('id', linkId)
      .single();
      
    if (linkError) {
      console.error('DEBUG: Error fetching link data:', linkError);
      throw linkError;
    }
    
    let productsLinkUrl = linkData?.products_link_url;
    
    // Ensure the URL has affiliate parameters
    if (productsLinkUrl) {
      const url = new URL(productsLinkUrl);
      if (!url.searchParams.has('irad')) {
        url.searchParams.append('irad', '1744632');
        url.searchParams.append('irmp', '6071614');
        productsLinkUrl = url.toString();
      }
    }
    
    console.log('DEBUG: Retrieved products_link_url with affiliate tracking:', productsLinkUrl);
    
    const insertData = {
      link_id: linkId,
      user_id: userId,
      meal_plan_id: mealPlanId,
      session_id: sessionIdToUse,
      is_anonymous: isAnonymous,
      products_link_url: productsLinkUrl,
      clicked_at: new Date().toISOString()
    };
    
    console.log('DEBUG: Click insert data:', insertData);
    
    const { data, error } = await supabase
      .from('instacart_link_clicks')
      .insert([insertData])
      .select();
      
    console.log('DEBUG: Click insert result:', { data, error });
    
    if (error) {
      console.error('DEBUG: Click error details:', error);
      throw error;
    }
    
    return data[0];
  } catch (error) {
    console.error('DEBUG: Error tracking Instacart link click:', error);
    // Don't throw the error to prevent disrupting the user experience
    return null;
  }
};

/**
 * Generates a random session ID for anonymous users
 * @returns {string} - A random session ID
 */
const generateSessionId = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}; 